import { Button } from "@src/components/ui-components/Buttons/Button/Button";
import { useAPISettings } from "@src/data-hooks/general-settings/settings.service";
import { usePingedCloudFunction } from "@src/data-hooks/generic/usePingedCloudFunction";
import { UpdatePropertyInput, updatePropertyInfoFields } from "@src/data-hooks/property/property.service";
import { isFieldEnabled } from "@src/utils/settings/settings.utils";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { FirebaseDataDateField } from "to-be-removed/LocalData/General/DataObjectField/Generic/FirebaseDataDateField";
import { Icon } from "../../../../components/deprecated/Icon/Icon";
import { Popup } from "../../../../components/deprecated/Popup/Popup";
import { PopupContainer } from "../../../../components/deprecated/PopupContainer/PopupContainer";
import { PopupTemplates } from "../../../../components/deprecated/PopupTemplates/PopupTemplates";
import { Text } from "../../../../components/deprecated/Text/Text";
import { ToastUtilities } from "../../../../libs/react-toastify/ToastUtilities";
import { FirebaseInfoFieldField } from "../../../../to-be-removed/LocalData/General/DataObjectField/CustomFields/PropertyFields";
import { DataObjectField } from "../../../../to-be-removed/LocalData/General/DataObjectField/DataObjectField";
import { DataObjectHelpers } from "../../../../to-be-removed/LocalData/General/DataObjects/DataObjectHelpers";
import { LocalData } from "../../../../to-be-removed/LocalData/General/LocalData";
import { PropertyData } from "../../../../to-be-removed/LocalData/Property/PropertyData";
import { PropertyFieldsSetting } from "../../../Companies/Company/Properties/PropertyFieldsSettings/PropertyFieldsSetting";
import styles from "./PropertyKeySettings.module.css";

export const propertyFinanceKeys = [
  "jointCost",
  "totalJointCost",
  "marketValue",
  "balanceSheetValue",
  "grossYield",
  "purchaseDate",
  "purchaseSum",
  "initialLoan",
  "remainingLoan",
];

type Props = {
  property: PropertyData;
  hideSettings: () => void;
  savedProperty: () => void;
};

export const PropertyKeySettings = (props: Props) => {
  const { settings } = useAPISettings({ propertyID: props.property.id });
  const [data, setData] = useState<{ [key: string]: any }>({});
  const [, setLastDate] = useState(new Date());
  const $updateProperty = useMutation(updatePropertyInfoFields);
  const _ = usePingedCloudFunction("updatePropertyInfoFields");

  const allFields: DataObjectField<any, any>[] = DataObjectHelpers.property.allFields();
  const adminCompany = props.property.companies.find((company) => company.members.getData(LocalData.currentUser.id)?.admin);
  const financeModuleActivated = props.property.moduleIsActivated("tenantEconomy");

  let fields: DataObjectField<any, any>[] = [
    DataObjectHelpers.unit.name,
    ...allFields.filter((field) => {
      if ([DataObjectHelpers.property.location.key, DataObjectHelpers.property.name.key].includes(field.key)) return false;

      return isFieldEnabled(field.key, "property", financeModuleActivated, settings?.data);
    }),
  ];

  useEffect(() => {
    const newData: { [key: string]: any } = {};

    for (const field of fields) {
      newData[field.key] = field.fullDecode(field.dataGetter(props.property));
    }
    setData(newData);
    setLastDate(new Date());
  }, [props.property]);

  const updateField = (field: string, value: any) => {
    setData({ ...data, [field]: value });
  };

  const saveSettings = () => {
    const input: UpdatePropertyInput = {
      data: {},
      propertyId: props.property.id,
    };
    for (const field of fields) {
      const fieldValue = data[field.key];
      if (field instanceof FirebaseInfoFieldField) {
        if (typeof fieldValue === "object" && fieldValue.value !== undefined) {
          input.data[field.key] = field.fullEncode(fieldValue);
        }
      } else if (fieldValue !== undefined) {
        if (field instanceof FirebaseDataDateField && fieldValue instanceof Date) {
          input.data[field.key] = { value: fieldValue.getTime() };
        } else {
          input.data[field.key] = { value: field.fullEncode(fieldValue) };
        }
      } else {
        input.data[field.key] = { value: null };
      }
    }

    const promise = $updateProperty
      .mutateAsync(input)
      .then(() => {
        props.property.loadInfoFields(() => {
          props.hideSettings();
          props.savedProperty();
        });
      })
      .catch((error) => {
        ToastUtilities.presentServerError(error);
      });

    ToastUtilities.presentSavingToast(promise);
  };

  const administrateFields = () => {
    if (adminCompany) {
      Popup.present((id) => (
        <PopupContainer
          className={styles.fieldPopup}
          id={id}
          children={<PropertyFieldsSetting company={adminCompany} fieldInformation="property" />}
          centered
          maxHeight="90%"
        />
      ));
    }
  };

  const onArchive = () => {
    if (props.property.archived) {
      PopupTemplates.presentVerifyAction(t("main.unarchiveProperty") + "?", t("main.propertyDearchivationCaution"), () => {
        ToastUtilities.presentSavingToast(
          props.property.archive().then(() => {
            props.property.load();
          })
        );
      });
    } else {
      PopupTemplates.presentVerifyAction(t("main.archiveProperty") + "?", t("main.propertyArchivationCaution"), () => {
        ToastUtilities.presentSavingToast(props.property.archive()).then(() => {
          props.property.load();
        });
      });
    }
  };

  return (
    <div>
      <Icon onClick={() => props.hideSettings()} name="close" float="right" />
      <table className={styles.table}>
        <tbody>
          {fields.map((field) => {
            return (
              <tr key={field.key}>
                <td>
                  <Text size="md" leadingIcon={field.iconName} marginRight={16}>
                    {field.title}
                  </Text>
                </td>
                <td>{field.inputElement?.(data[field.key], (value) => updateField(field.key, value), { width: "300px" })}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <Button marginTop={32} onClick={() => saveSettings()} fullWidth loading={$updateProperty.isLoading} disabled={$updateProperty.isLoading}>
        {t("main.save")}
      </Button>
      <Button
        data-cy="property-archive-button-key-settings"
        marginTop={16}
        type="secondary"
        leadingIcon="archive"
        onClick={onArchive}
        fullWidth
        disabled={$updateProperty.isLoading}
      >
        {props.property.archived ? t("main.unarchiveProperty") : t("main.archiveProperty")}
      </Button>

      {adminCompany && (
        <Button
          type="text"
          leadingIcon="help"
          marginTop={16}
          onClick={() => administrateFields()}
          style={{
            fontStyle: "italic",
          }}
          disabled={$updateProperty.isLoading}
        >
          {t("main.editInfoFieldsFromPropertyText")}
        </Button>
      )}
    </div>
  );
};
